/**
 * AI Suggestion Dialog
 *
 * Shows AI-generated suggestions in a dialog
 * Allows user to accept, regenerate, or customize
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import {
  Close,
  AutoAwesome,
  Refresh,
  CheckCircle,
  Edit,
  Info
} from '@mui/icons-material';

const AISuggestionDialog = ({
  open,
  onClose,
  title,
  suggestions,
  loading,
  error,
  onAccept,
  onRegenerate,
  type
}) => {
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleAccept = () => {
    if (suggestions && suggestions.length > 0) {
      onAccept(suggestions[selectedIndex]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 400
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesome sx={{ color: 'primary.main' }} />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Loading State */}
        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" py={6}>
            <CircularProgress size={50} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Generating AI suggestions...
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Suggestions */}
        {!loading && !error && suggestions && suggestions.length > 0 && (
          <Box>
            {/* Privacy Notice */}
            <Alert
              severity="info"
              icon={<Info />}
              sx={{ mb: 3, bgcolor: alpha(theme.palette.info.main, 0.1) }}
            >
              AI-generated suggestions. Please review and customize as needed.
            </Alert>

            {/* Render based on type */}
            {type === 'businessName' && renderBusinessNames(suggestions, selectedIndex, setSelectedIndex, theme)}
            {type === 'services' && renderServices(suggestions, selectedIndex, setSelectedIndex, theme)}
            {type === 'pricing' && renderPricing(suggestions, theme)}
            {type === 'availability' && renderAvailability(suggestions, theme)}
            {type === 'description' && renderDescription(suggestions, theme)}
            {type === 'help' && renderHelp(suggestions, theme)}
          </Box>
        )}

        {/* No suggestions */}
        {!loading && !error && (!suggestions || suggestions.length === 0) && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              No suggestions available. Try regenerating.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>

        {onRegenerate && !loading && (
          <Button
            startIcon={<Refresh />}
            onClick={onRegenerate}
            disabled={loading}
          >
            Regenerate
          </Button>
        )}

        {suggestions && suggestions.length > 0 && !loading && (
          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={handleAccept}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
            }}
          >
            Use This Suggestion
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/**
 * Render business name suggestions
 */
function renderBusinessNames(suggestions, selectedIndex, setSelectedIndex, theme) {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Select a business name:
      </Typography>
      <List>
        {suggestions.map((item, index) => (
          <React.Fragment key={index}>
            <ListItem
              button
              selected={selectedIndex === index}
              onClick={() => setSelectedIndex(index)}
              sx={{
                borderRadius: 2,
                mb: 1,
                border: selectedIndex === index
                  ? `2px solid ${theme.palette.primary.main}`
                  : '1px solid transparent',
                '&:hover': {
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="h6" fontWeight="bold">
                    {item.name}
                  </Typography>
                }
                secondary={item.reason}
              />
              {selectedIndex === index && <CheckCircle color="primary" />}
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
}

/**
 * Render service suggestions
 */
function renderServices(suggestions, selectedIndex, setSelectedIndex, theme) {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Select a service to add:
      </Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        {suggestions.map((service, index) => (
          <Card
            key={index}
            elevation={selectedIndex === index ? 4 : 1}
            sx={{
              cursor: 'pointer',
              border: selectedIndex === index
                ? `2px solid ${theme.palette.primary.main}`
                : '1px solid transparent',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => setSelectedIndex(index)}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="start">
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {service.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {service.description}
                  </Typography>
                </Box>
                {selectedIndex === index && <CheckCircle color="primary" />}
              </Box>
              <Box display="flex" gap={1}>
                <Chip label={`${service.duration} minutes`} size="small" />
                <Chip label={`$${service.price}`} size="small" color="primary" />
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Render pricing suggestion
 */
function renderPricing(suggestion, theme) {
  if (!suggestion) return null;

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recommended Pricing
          </Typography>

          <Box display="flex" gap={2} my={3}>
            <Box flex={1} textAlign="center">
              <Typography variant="caption" color="text.secondary">
                Minimum
              </Typography>
              <Typography variant="h4" color="text.secondary">
                ${suggestion.min}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box flex={1} textAlign="center">
              <Typography variant="caption" color="primary">
                Recommended
              </Typography>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                ${suggestion.recommended}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box flex={1} textAlign="center">
              <Typography variant="caption" color="text.secondary">
                Maximum
              </Typography>
              <Typography variant="h4" color="text.secondary">
                ${suggestion.max}
              </Typography>
            </Box>
          </Box>

          <Alert severity="info" icon={<Info />}>
            {suggestion.rationale}
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
}

/**
 * Render availability suggestion
 */
function renderAvailability(suggestion, theme) {
  if (!suggestion) return null;

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Suggested Working Hours:
      </Typography>

      <List>
        {days.map((day, index) => {
          const dayData = suggestion[day];
          if (!dayData) return null;

          return (
            <ListItem
              key={day}
              sx={{
                bgcolor: dayData.enabled
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.grey[500], 0.1),
                borderRadius: 2,
                mb: 1
              }}
            >
              <ListItemText
                primary={dayLabels[index]}
                secondary={
                  dayData.enabled
                    ? `${dayData.start} - ${dayData.end}`
                    : 'Closed'
                }
              />
              {dayData.enabled && <CheckCircle color="success" />}
            </ListItem>
          );
        })}
      </List>

      {suggestion.rationale && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {suggestion.rationale}
        </Alert>
      )}
    </Box>
  );
}

/**
 * Render description
 */
function renderDescription(description, theme) {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Suggested Description
        </Typography>
        <Typography variant="body1" paragraph>
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
}

/**
 * Render help answer
 */
function renderHelp(answer, theme) {
  return (
    <Card>
      <CardContent>
        <Typography variant="body1">
          {answer}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default AISuggestionDialog;
